import React, { useState, useEffect } from 'react';
import { auditService, usersService, getErrorMessage } from '../services/api';
import {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardContent,
  NativeSelect,
  NativeBadge,
  NativeAlert,
  NativeTable,
  NativeTableHeader,
  NativeTableHead,
  NativeTableBody,
  NativeTableRow,
  NativeTableCell,
  EmptyState
} from '../components/NativeComponents';
import { 
  Loader2, 
  AlertCircle,
  RefreshCw,
  Shield,
  AlertTriangle
} from 'lucide-react';

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [failedLogins, setFailedLogins] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featureAvailable, setFeatureAvailable] = useState(true);
  
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [limitFilter, setLimitFilter] = useState('100');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Paralel istekler yerine sıralı yap - hata izolasyonu için
      let logsData = [];
      let usersData = [];
      
      // Kullanıcı listesini çek
      try {
        usersData = await usersService.list();
        setUsers(Array.isArray(usersData) ? usersData : []);
      } catch (userErr) {
        console.warn('Kullanıcı listesi yüklenemedi:', userErr);
        setUsers([]);
      }
      
      // Audit logları çek
      try {
        logsData = await auditService.list({ 
          action: actionFilter || undefined, 
          user_id: userFilter || undefined, 
          limit: parseInt(limitFilter) || 100 
        });
        
        if (Array.isArray(logsData)) {
          setLogs(logsData);
          setFeatureAvailable(true);
        } else if (logsData && typeof logsData === 'object') {
          // Eğer obje dönerse (örn: {logs: [...]} yapısı)
          const extractedLogs = logsData.logs || logsData.items || logsData.data || [];
          setLogs(Array.isArray(extractedLogs) ? extractedLogs : []);
          setFeatureAvailable(true);
        } else {
          setLogs([]);
          setFeatureAvailable(logsData !== null && logsData !== undefined);
        }
      } catch (logErr) {
        console.warn('Audit logları yüklenemedi:', logErr);
        if (logErr.status === 404) {
          setFeatureAvailable(false);
        }
        setLogs([]);
      }

      // Başarısız giriş denemeleri
      try {
        const failedData = await auditService.failedLogins(50);
        setFailedLogins(Array.isArray(failedData) ? failedData : []);
      } catch (failedErr) {
        console.warn('Başarısız girişler yüklenemedi:', failedErr);
        setFailedLogins([]);
      }
    } catch (err) {
      console.error('Genel veri çekme hatası:', err);
      if (err.status === 404) {
        setFeatureAvailable(false);
        setLogs([]);
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [actionFilter, userFilter, limitFilter]);

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.full_name || user?.email || userId || '-';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success': return <NativeBadge variant="success">Basarili</NativeBadge>;
      case 'failure': return <NativeBadge variant="danger">Basarisiz</NativeBadge>;
      default: return <NativeBadge variant="secondary">{status}</NativeBadge>;
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!featureAvailable) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="audit-logs-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>Denetim Kayitlari</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>Sistem aktivite kayitlari</p>
        </div>
        <NativeAlert variant="warning" icon={AlertTriangle}>
          Denetim kayitlari ozelligi su an aktif degil. Bu ozellik gelecek guncellemelerde eklenecektir.
        </NativeAlert>
      </div>
    );
  }

  if (error) {
    return (
      <NativeAlert variant="danger" icon={AlertCircle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <NativeButton variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Tekrar Dene
          </NativeButton>
        </div>
      </NativeAlert>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="audit-logs-page">
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>Denetim Kayitlari</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>Sistem aktivite kayıtlarını görüntüleyin</p>
        </div>
        <NativeButton variant="outline" onClick={fetchData}>
          <RefreshCw style={{ width: '16px', height: '16px' }} />
          Yenile
        </NativeButton>
      </div>

      {/* Failed Logins Alert */}
      {failedLogins.length > 0 && (
        <NativeAlert variant="danger" icon={AlertTriangle}>
          Son 24 saatte {failedLogins.length} basarisiz giris denemesi tespit edildi.
        </NativeAlert>
      )}

      {/* Filters */}
      <NativeCard>
        <NativeCardContent>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            <NativeSelect
              id="actionFilter"
              value={actionFilter || "all"}
              onChange={(e) => setActionFilter(e.target.value === "all" ? "" : e.target.value)}
              options={[
                { value: 'all', label: 'Tum Aksiyonlar' },
                { value: 'login', label: 'Login' },
                { value: 'logout', label: 'Logout' },
                { value: 'create', label: 'Oluşturma' },
                { value: 'update', label: 'Güncelleme' },
                { value: 'delete', label: 'Silme' }
              ]}
            />
            <NativeSelect
              id="userFilter"
              value={userFilter || "all"}
              onChange={(e) => setUserFilter(e.target.value === "all" ? "" : e.target.value)}
              placeholder="Tüm Kullanıcılar"
              options={[{ value: 'all', label: 'Tüm Kullanıcılar' }, ...users.map((user) => ({ value: user.id, label: user.full_name || user.email }))]}
            />
            <NativeSelect
              id="limitFilter"
              value={limitFilter}
              onChange={(e) => setLimitFilter(e.target.value)}
              options={[
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' },
                { value: '500', label: '500' }
              ]}
            />
          </div>
        </NativeCardContent>
      </NativeCard>

      {/* Logs Table */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={Shield}>Kayit Listesi ({logs.length})</NativeCardTitle>
        </NativeCardHeader>
        <NativeCardContent style={{ padding: 0 }}>
          {logs.length === 0 ? (
            <EmptyState icon={Shield} title="Denetim kaydı bulunamadı" description="Filtreleme kriterlerinize uygun kayıt yok" />
          ) : (
            <NativeTable>
              <NativeTableHeader>
                <NativeTableHead>Tarih</NativeTableHead>
                <NativeTableHead>Kullanici</NativeTableHead>
                <NativeTableHead>Aksiyon</NativeTableHead>
                <NativeTableHead>Durum</NativeTableHead>
                <NativeTableHead>Detaylar</NativeTableHead>
              </NativeTableHeader>
              <NativeTableBody>
                {logs.map((log, index) => (
                  <NativeTableRow key={log.id || index} data-testid={`audit-row-${index}`}>
                    <NativeTableCell style={{ whiteSpace: 'nowrap' }}>
                      {log.timestamp ? new Date(log.timestamp).toLocaleString('tr-TR') : '-'}
                    </NativeTableCell>
                    <NativeTableCell>{getUserName(log.user_id)}</NativeTableCell>
                    <NativeTableCell><NativeBadge variant="outline">{log.action}</NativeBadge></NativeTableCell>
                    <NativeTableCell>{getStatusBadge(log.status)}</NativeTableCell>
                    <NativeTableCell style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {typeof log.details === 'object' ? JSON.stringify(log.details).substring(0, 50) + '...' : log.details || '-'}
                    </NativeTableCell>
                  </NativeTableRow>
                ))}
              </NativeTableBody>
            </NativeTable>
          )}
        </NativeCardContent>
      </NativeCard>
    </div>
  );
}
